<script setup lang="ts">
import { ArrowLeft, ArrowRight, Check, Eye, EyeOff, LockKeyhole, Sparkles } from 'lucide-vue-next'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BrandMark from '@/components/BrandMark.vue'
import { useAuthStore } from '@/stores/auth'

const props = defineProps<{ mode: 'login' | 'signup' }>()
const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()

const name = ref('')
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const isSignup = computed(() => props.mode === 'signup')

async function submit(): Promise<void> {
  try {
    if (isSignup.value) {
      await authStore.register(name.value, email.value, password.value)
      await router.push('/verify-email')
      return
    }
    await authStore.login(email.value, password.value)
    const redirect =
      typeof route.query.redirect === 'string' ? route.query.redirect : '/app/manager'
    await router.push(redirect)
  } catch {
    // The store exposes a safe user-facing error.
  }
}

// Returning from a redirect sign-in lands back here already signed in.
onMounted(() => {
  if (authStore.isAuthenticated) void router.replace(typeof route.query.redirect === 'string' ? route.query.redirect : '/billing')
})

async function google(): Promise<void> {
  try {
    await authStore.continueWithGoogle()
    await router.push('/billing')
  } catch {
    // The store exposes a safe user-facing error.
  }
}
</script>

<template>
  <div class="auth-page">
    <section class="auth-story">
      <RouterLink class="auth-story__brand" to="/"><BrandMark inverse /></RouterLink>
      <div class="auth-story__content">
        <span class="auth-story__kicker"><Sparkles :size="16" /> Persistent by design</span>
        <h1>Your goals deserve an operating system.</h1>
        <p>
          One Manager that learns how you work, builds specialized teams, and keeps every project
          moving.
        </p>
        <div class="auth-quote">
          <p>“I stopped coordinating tools and started making decisions.”</p>
          <span>— Early design partner</span>
        </div>
      </div>
      <div class="auth-story__checks">
        <span><Check :size="14" /> Your data stays tenant-isolated</span>
        <span><Check :size="14" /> High-impact work requires approval</span>
      </div>
    </section>

    <section class="auth-form-panel">
      <RouterLink class="back-link" to="/"><ArrowLeft :size="16" /> Back</RouterLink>
      <div class="auth-form-wrap">
        <div class="auth-heading">
          <span class="auth-step">{{ isSignup ? 'STEP 1 OF 2' : 'WELCOME BACK' }}</span>
          <h2>{{ isSignup ? 'Create your workspace' : 'Sign in to your workspace' }}</h2>
          <p>
            {{
              isSignup
                ? 'Start with your identity. Billing and Manager activation follow verification.'
                : 'Your Manager and portfolio are waiting.'
            }}
          </p>
        </div>

        <button class="google-button" type="button" :disabled="authStore.loading" @click="google">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285f4"
              d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.92h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.33 2.98-7.4Z"
            />
            <path
              fill="#34a853"
              d="M12 22c2.7 0 4.98-.9 6.63-2.36l-3.24-2.54c-.9.6-2.05.96-3.39.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.62A10 10 0 0 0 12 22Z"
            />
            <path
              fill="#fbbc05"
              d="M6.39 13.93A6.02 6.02 0 0 1 6.08 12c0-.67.11-1.32.31-1.93V7.45H3.04A10 10 0 0 0 2 12c0 1.62.39 3.15 1.04 4.55l3.35-2.62Z"
            />
            <path
              fill="#ea4335"
              d="M12 5.94c1.47 0 2.79.51 3.83 1.5l2.87-2.87A9.62 9.62 0 0 0 12 2a10 10 0 0 0-8.96 5.45l3.35 2.62C7.18 7.7 9.39 5.94 12 5.94Z"
            />
          </svg>
          Continue with Google
        </button>

        <div class="form-divider"><span>or continue with email</span></div>

        <form class="auth-form" @submit.prevent="submit">
          <label v-if="isSignup">
            <span>Your name</span>
            <input
              v-model="name"
              type="text"
              autocomplete="name"
              placeholder="Ada Lovelace"
              minlength="2"
              maxlength="80"
              required
            />
          </label>
          <label>
            <span>Work email</span>
            <input
              v-model="email"
              type="email"
              autocomplete="email"
              placeholder="you@company.com"
              required
            />
          </label>
          <label>
            <span>Password</span>
            <div class="password-field">
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                :autocomplete="isSignup ? 'new-password' : 'current-password'"
                placeholder="At least 8 characters"
                minlength="8"
                required
              />
              <button
                type="button"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                @click="showPassword = !showPassword"
              >
                <EyeOff v-if="showPassword" :size="17" /><Eye v-else :size="17" />
              </button>
            </div>
          </label>

          <div v-if="authStore.error" class="form-error" role="alert">{{ authStore.error }}</div>

          <button
            class="button button--primary auth-submit"
            type="submit"
            :disabled="authStore.loading"
          >
            {{ authStore.loading ? 'Working…' : isSignup ? 'Create workspace' : 'Sign in' }}
            <ArrowRight v-if="!authStore.loading" :size="17" />
          </button>
        </form>

        <p class="auth-switch">
          {{ isSignup ? 'Already have an account?' : 'New to SkipHR?' }}
          <RouterLink :to="isSignup ? '/login' : '/signup'">{{
            isSignup ? 'Sign in' : 'Create workspace'
          }}</RouterLink>
        </p>
        <p class="auth-legal">
          <LockKeyhole :size="13" /> Firebase Authentication secures your identity. Billing never
          activates from a client redirect.
        </p>
      </div>
    </section>
  </div>
</template>
